'use client';

import { useState, useEffect, useCallback } from 'react';

const CEO_PIN = '5050';

interface FaqEntry {
  id: string;
  category: string;
  question: string;
  answer: string;
  sort_order: number;
  published: boolean;
  created_at: string;
  updated_at: string;
}

const CATEGORIES = ['worker', 'contractor', 'general'] as const;
const CATEGORY_LABELS: Record<string, string> = {
  worker: '👷 Workers',
  contractor: '🏗️ Contractors',
  general: '📋 General',
};

export default function CEOFaqEditor() {
  const [faqs, setFaqs] = useState<Record<string, FaqEntry[]>>({ worker: [], contractor: [], general: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('worker');
  const [editing, setEditing] = useState<string | null>(null);
  const [editQuestion, setEditQuestion] = useState('');
  const [editAnswer, setEditAnswer] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newCategory, setNewCategory] = useState<string>('worker');
  const [newQuestion, setNewQuestion] = useState('');
  const [newAnswer, setNewAnswer] = useState('');
  const [saving, setSaving] = useState(false);

  const headers = { 'x-ceo-pin': CEO_PIN, 'Content-Type': 'application/json' };

  const fetchFaqs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/faq?pin=${CEO_PIN}`);
      const json = await res.json();
      setFaqs(json.data || { worker: [], contractor: [], general: [] });
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFaqs(); }, [fetchFaqs]);

  const startEdit = (entry: FaqEntry) => {
    setEditing(entry.id);
    setEditQuestion(entry.question);
    setEditAnswer(entry.answer);
  };

  const saveEdit = async (id: string) => {
    setSaving(true);
    await fetch('/api/faq', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ id, question: editQuestion, answer: editAnswer }),
    });
    setEditing(null);
    setSaving(false);
    fetchFaqs();
  };

  const togglePublished = async (entry: FaqEntry) => {
    await fetch('/api/faq', {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ id: entry.id, published: !entry.published }),
    });
    fetchFaqs();
  };

  const deleteEntry = async (id: string) => {
    if (!confirm('Delete this FAQ?')) return;
    await fetch('/api/faq', {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ id }),
    });
    fetchFaqs();
  };

  const createEntry = async () => {
    if (!newQuestion.trim() || !newAnswer.trim()) return;
    setSaving(true);
    const maxOrder = Math.max(...(faqs[newCategory]?.map(f => f.sort_order) || [0]), 0);
    await fetch('/api/faq', {
      method: 'POST',
      headers,
      body: JSON.stringify({
        category: newCategory,
        question: newQuestion,
        answer: newAnswer,
        sort_order: maxOrder + 1,
      }),
    });
    setNewQuestion('');
    setNewAnswer('');
    setShowNew(false);
    setSaving(false);
    fetchFaqs();
  };

  const moveEntry = async (entry: FaqEntry, direction: 'up' | 'down') => {
    const list = faqs[entry.category] || [];
    const idx = list.findIndex(f => f.id === entry.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;

    const other = list[swapIdx];
    await Promise.all([
      fetch('/api/faq', { method: 'PATCH', headers, body: JSON.stringify({ id: entry.id, sort_order: other.sort_order }) }),
      fetch('/api/faq', { method: 'PATCH', headers, body: JSON.stringify({ id: other.id, sort_order: entry.sort_order }) }),
    ]);
    fetchFaqs();
  };

  const currentFaqs = faqs[activeTab] || [];
  const totalCount = Object.values(faqs).reduce((sum, arr) => sum + arr.length, 0);
  const publishedCount = Object.values(faqs).reduce((sum, arr) => sum + arr.filter(f => f.published).length, 0);

  return (
    <div className="min-h-screen bg-gray-950 text-white pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-gray-950/95 backdrop-blur border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold">📖 FAQ Manager</h1>
            <p className="text-xs text-gray-500">{publishedCount} published · {totalCount} total</p>
          </div>
          <button
            onClick={() => { setShowNew(true); setNewCategory(activeTab); }}
            className="text-sm bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg transition"
          >
            + Add FAQ
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 mt-3">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`flex-1 py-2 text-xs font-medium rounded-lg transition ${
                activeTab === cat ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {CATEGORY_LABELS[cat]} ({faqs[cat]?.length || 0})
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 py-4 max-w-lg mx-auto space-y-3">
        {loading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-white/20 border-t-white rounded-full" />
          </div>
        )}

        {/* New FAQ form */}
        {showNew && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-300">New FAQ</span>
              <button onClick={() => setShowNew(false)} className="text-xs text-gray-500">Cancel</button>
            </div>
            <select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-xs text-gray-200"
            >
              {CATEGORIES.map(c => <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>)}
            </select>
            <input
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              placeholder="Question"
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-sm text-gray-200 placeholder:text-gray-600"
            />
            <textarea
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              placeholder="Answer"
              rows={3}
              className="w-full rounded-md bg-gray-800 border border-white/10 px-2 py-1.5 text-sm text-gray-200 placeholder:text-gray-600 resize-none"
            />
            <button
              onClick={createEntry}
              disabled={saving || !newQuestion.trim() || !newAnswer.trim()}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-sm py-1.5 rounded-lg transition"
            >
              {saving ? 'Saving...' : 'Create FAQ'}
            </button>
          </div>
        )}

        {/* FAQ entries */}
        {!loading && currentFaqs.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p className="text-4xl mb-3">📖</p>
            <p className="text-sm">No FAQs in this category</p>
          </div>
        )}

        {currentFaqs.map((entry, idx) => (
          <div
            key={entry.id}
            className={`rounded-xl p-3 space-y-2 border ${
              entry.published
                ? 'bg-white/5 border-white/10'
                : 'bg-gray-500/5 border-gray-500/20 opacity-60'
            }`}
          >
            {editing === entry.id ? (
              // Edit mode
              <div className="space-y-2">
                <input
                  value={editQuestion}
                  onChange={(e) => setEditQuestion(e.target.value)}
                  className="w-full rounded-md bg-gray-800 border border-white/20 px-2 py-1.5 text-sm text-gray-200"
                />
                <textarea
                  value={editAnswer}
                  onChange={(e) => setEditAnswer(e.target.value)}
                  rows={4}
                  className="w-full rounded-md bg-gray-800 border border-white/20 px-2 py-1.5 text-sm text-gray-200 resize-none"
                />
                <div className="flex gap-2">
                  <button onClick={() => saveEdit(entry.id)} disabled={saving} className="text-xs px-3 py-1 bg-blue-500/20 text-blue-300 rounded-lg">
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => setEditing(null)} className="text-xs px-3 py-1 bg-white/5 text-gray-400 rounded-lg">Cancel</button>
                </div>
              </div>
            ) : (
              // View mode
              <>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug">{entry.question}</p>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{entry.answer}</p>
                  </div>
                  <span className="text-xs text-gray-600 shrink-0">#{entry.sort_order}</span>
                </div>

                <div className="flex items-center gap-1 flex-wrap">
                  <button onClick={() => startEdit(entry)} className="text-xs px-2 py-1 bg-white/5 text-gray-400 rounded hover:bg-white/10">✏️ Edit</button>
                  <button onClick={() => togglePublished(entry)} className="text-xs px-2 py-1 bg-white/5 rounded hover:bg-white/10">
                    {entry.published ? <span className="text-green-400">✅ Published</span> : <span className="text-gray-500">⬜ Draft</span>}
                  </button>
                  <button onClick={() => moveEntry(entry, 'up')} disabled={idx === 0} className="text-xs px-2 py-1 bg-white/5 text-gray-400 rounded hover:bg-white/10 disabled:opacity-30">↑</button>
                  <button onClick={() => moveEntry(entry, 'down')} disabled={idx === currentFaqs.length - 1} className="text-xs px-2 py-1 bg-white/5 text-gray-400 rounded hover:bg-white/10 disabled:opacity-30">↓</button>
                  <button onClick={() => deleteEntry(entry.id)} className="text-xs px-2 py-1 bg-red-500/10 text-red-400 rounded hover:bg-red-500/20 ml-auto">🗑</button>
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
